import css from 'styled-jsx/css';
import theme from './themeProperties';

const {
  light,
  dark,
  fontSizes,
  primaryColor,
  primaryDarkColor,
  primaryDarkColorTransparent,
  defaultFontFamily,
  primaryLightColor,
  topProjectBackgroundColor,
  nonDonatableProjectBackgroundColor,
  primaryColorNew,
  horizontalLineColorNew,
  exploreDescriptionBackground,
  designSystem,
} = theme;

type TenantFont = {
  primaryFontFamily?: string | null;
  secondaryFontFamily?: string | null;
};

/**
 * Builds the global CSS variables. Tenant fonts are passed in (the caller reads
 * them from the tenant store) so this module is synchronous — it previously
 * fetched the tenant config at module load via a top-level await, which made
 * the whole theme module (and its importers) async.
 */
const getGlobalStyles = (font?: TenantFont) => {
  const primaryFontFamily = font?.primaryFontFamily || defaultFontFamily;
  const secondaryFontFamily = font?.secondaryFontFamily || defaultFontFamily;

  return css.global`
    :root {
      --primary-font-family: ${primaryFontFamily};
      --secondary-font-family: ${secondaryFontFamily};
      --font-xx-extra-small: ${fontSizes.fontXXSmall};
      --font-x-extra-small: ${fontSizes.fontXSmall};
      --font-small: ${fontSizes.fontSmall};
      --font-sixteen: ${fontSizes.fontSixteen};
      --font-medium: ${fontSizes.fontMedium};
      --font-large: ${fontSizes.fontLarge};
      --font-x-large: ${fontSizes.fontXLarge};
      --font-xx-large: ${fontSizes.fontXXLarge};
      --font-xxx-large: ${fontSizes.fontXXXLarge};
      --primary-color: ${primaryColor};
      --primary-dark-color: ${primaryDarkColor};
      --primary-dark-color-transparent: ${primaryDarkColorTransparent};
      --primary-light-color: ${primaryLightColor};
      --top-project-background-color: ${topProjectBackgroundColor};
      --non-donatable-project-background-color: ${nonDonatableProjectBackgroundColor};
      --primary-color-new: ${primaryColorNew};
      --explore-description-background-color-new: ${exploreDescriptionBackground};
      --horizontal-line-color: ${horizontalLineColorNew};
      --sky-blue-color: ${theme.skyBlueColor};
      --ds-primary-color: ${designSystem.colors.primaryColor};
      --ds-primary-color-transparent-7: ${designSystem.colors
        .primaryColorTransparent7};
      --ds-primary-color-transparent-10: ${designSystem.colors
        .primaryColorTransparent10};
      --ds-forest-green: ${designSystem.colors.forestGreen};
      --ds-forest-green-transparent-20: ${designSystem.colors
        .forestGreenTransparent20};
      --ds-forest-green-transparent-40: ${designSystem.colors
        .forestGreenTransparent40};
      --ds-leaf-green: ${designSystem.colors.leafGreen};
      --ds-leaf-green-transparent-10: ${designSystem.colors
        .leafGreenTransparent10};
      --ds-bright-green: ${designSystem.colors.brightGreen};
      --ds-mint-gradient: ${designSystem.colors.mintGradient};
      --ds-mint-gradient-start: ${designSystem.colors.mintGradientStart};
      --ds-soft-green: ${designSystem.colors.softGreen};
      --ds-golden-yellow: ${designSystem.colors.goldenYellow};
      --ds-soft-yellow: ${designSystem.colors.softYellow};
      --ds-dark-chocolate: ${designSystem.colors.darkChocolate};
      --ds-soft-dark-chocolate: ${designSystem.colors.softDarkChocolate};
      --ds-milk-chocolate: ${designSystem.colors.milkChocolate};
      --ds-soft-milk-chocolate: ${designSystem.colors.softMilkChocolate};
      --ds-purple-sky: ${designSystem.colors.purpleSky};
      --ds-purple-sky-transparent-10: ${designSystem.colors
        .purpleSkyTransparent10};
      --ds-purple-sky-transparent-20: ${designSystem.colors
        .purpleSkyTransparent20};
      --ds-soft-sky: ${designSystem.colors.softSky};
      --ds-ocean-blue: ${designSystem.colors.oceanBlue};
      --ds-soft-blue: ${designSystem.colors.softBlue};
      --ds-deep-purple: ${designSystem.colors.deepPurple};
      --ds-deep-purple-transparent-10: ${designSystem.colors
        .deepPurpleTransparent10};
      --ds-soft-purple: ${designSystem.colors.softPurple};
      --ds-fire-red: ${designSystem.colors.fireRed};
      --ds-fire-red-transparent-10: ${designSystem.colors.fireRedTransparent10};
      --ds-soft-red: ${designSystem.colors.softRed};
      --ds-white: ${designSystem.colors.white};
      --ds-white-transparent-10: ${designSystem.colors.whiteTransparent10};
      --ds-background-base: ${designSystem.colors.backgroundBase};
      --ds-core-text: ${designSystem.colors.coreText};
      --ds-soft-text: ${designSystem.colors.softText};
      --ds-soft-text-2: ${designSystem.colors.softText2};
      --ds-hint-text: ${designSystem.colors.hintText};
      --ds-warm-green: ${designSystem.colors.warmGreen};
      --ds-warm-green-transparent-10: ${designSystem.colors
        .warmGreenTransparent10};
      --ds-sunrise-orange: ${designSystem.colors.sunriseOrange};
      --ds-sunrise-orange-transparent-10: ${designSystem.colors
        .sunriseOrangeTransparent10};
      --ds-bright-yellow: ${designSystem.colors.brightYellow};
      --ds-warm-blue: ${designSystem.colors.warmBlue};
      --ds-warm-blue-transparent-10: ${designSystem.colors
        .warmBlueTransparent10};
      --ds-warm-blue-transparent-20: ${designSystem.colors
        .warmBlueTransparent20};
      --ds-sky-blue: ${designSystem.colors.skyBlue};
      --ds-bright-blue: ${designSystem.colors.brightBlue};
      --ds-warm-red: ${designSystem.colors.warmRed};
      --ds-hot-pink: ${designSystem.colors.hotPink};
      --ds-base-grey: ${designSystem.colors.baseGrey};
      --ds-dark-grey: ${designSystem.colors.darkGrey};
      --ds-medium-grey: ${designSystem.colors.mediumGrey};
      --ds-medium-grey-transparent-30: ${designSystem.colors
        .mediumGreyTransparent30};
      --ds-medium-grey-transparent-50: ${designSystem.colors
        .mediumGreyTransparent50};
      --ds-medium-grey-transparent-70: ${designSystem.colors
        .mediumGreyTransparent70};
      --ds-deep-blue: ${designSystem.colors.deepBlue};
      --ds-deep-green: ${designSystem.colors.deepGreen};
    }
    .theme-light {
      --primary-font-color: ${light.primaryFontColor};
      --divider-color: ${light.dividerColor};
      --secondary-color: ${light.secondaryColor};
      --background-color-dark: ${light.backgroundColorDark};
      --background-color: ${light.backgroundColor};
      --highlight-background: ${light.highlightBackground};
      --light: ${light.light};
      --dark: ${light.dark};
      --danger-color: ${light.dangerColor};
      --safe-color: ${light.safeColor};
      --un-decade-fill: ${light.unDecadeFill};
      --mapbox-icon: ${light.mapboxIcon};
      --background-base: ${light.backgroundBase};
      --disabled-font-color: ${light.disabledFontColor};
      --bold-font-color-new: ${light.boldFontColorNew};
      --divider-color-new: ${light.dividerColorNew};
      --danger-color-new: ${light.dangerColorNew};
      --border-color: ${light.borderColor};
      --top-project-color: ${topProjectBackgroundColor};
    }
    .theme-dark {
      --primary-font-color: ${dark.primaryFontColor};
      --divider-color: ${dark.dividerColor};
      --secondary-color: ${dark.secondaryColor};
      --background-color-dark: ${dark.backgroundColorDark};
      --background-color: ${dark.backgroundColor};
      --highlight-background: ${dark.highlightBackground};
      --light: ${dark.light};
      --dark: ${dark.dark};
      --danger-color: ${dark.dangerColor};
      --safe-color: ${dark.safeColor};
      --un-decade-fill: ${dark.unDecadeFill};
      --mapbox-icon: ${dark.mapboxIcon};
      --background-base: ${dark.backgroundBase};
      --disabled-font-color: ${dark.disabledFontColor};
    }
  `;
};

export default getGlobalStyles;
